import { useId, useState, type ChangeEvent } from 'react'
import { ImageUp } from 'lucide-react'
import { cn } from '../lib/cn'

/**
 * The dashed "Upload Image" tile the signup and profile frames use.
 *
 * Holds the chosen file locally and shows a preview; it does not upload —
 * the caller requests a presigned URL and puts the bytes there itself, since
 * every caller does that against a different category and record.
 */
export type ImageUploadProps = {
  label: string
  value: File | null
  onValueChange: (file: File | null) => void
  /** Rejects anything larger, in bytes. Phone cameras produce big files. */
  maxBytes?: number
  accept?: string
  disabled?: boolean
  /** Shown until a new file is picked — an already-uploaded photo, if any. */
  previewUrl?: string | undefined
  /**
   * What the empty tile says. Defaults to "Upload Image".
   *
   * <p>The document frames name the thing being asked for — "Recto", "Verso",
   * "Uploader ici" — and a tile that says "Upload Image" beside a label saying
   * "Scan Carte grise" is asking the same question twice in two vocabularies.
   */
  placeholder?: string
  /**
   * `square` is the 100px avatar tile; `block` is the full-width dropzone the
   * document frames draw.
   */
  shape?: 'square' | 'block'
}

const DEFAULT_MAX = 5 * 1024 * 1024

export function ImageUpload({
  label,
  value,
  onValueChange,
  maxBytes = DEFAULT_MAX,
  accept = 'image/png,image/jpeg,image/webp',
  disabled = false,
  previewUrl,
  placeholder,
  shape = 'square',
}: ImageUploadProps) {
  const id = useId()
  const [error, setError] = useState<string | null>(null)
  const preview = value ? URL.createObjectURL(value) : (previewUrl ?? null)

  function pick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null
    if (file && file.size > maxBytes) {
      setError(`Image trop lourde (max ${Math.round(maxBytes / 1024 / 1024)} Mo).`)
      onValueChange(null)
      return
    }
    setError(null)
    onValueChange(file)
  }

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-xs text-slate-600">
        {label}
      </label>

      <label
        htmlFor={id}
        className={cn(
          'group flex cursor-pointer flex-col items-center justify-center gap-1',
          shape === 'square' ? 'size-[100px]' : 'h-[104px] w-full',
          'rounded-md border border-dashed border-slate-400 bg-slate-100 text-center',
          // Answers the pointer and the finger: the dashed box is the only cue
          // that this is a control at all, so it has to behave like one.
          'transition-[background-color,border-color,transform] duration-[--duration-fast]',
          'hover:border-brand-400 ease-[--ease-out-soft] hover:bg-slate-200/70 active:scale-[0.98]',
          // slate-600 rather than the frame's slate-500: 10px slate-500 on a
          // slate-100 fill measures 4.34:1, under the 4.5:1 the gate requires.
          'text-2xs overflow-hidden text-slate-600',
          'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        {preview ? (
          // The chosen file appearing is the confirmation that it was accepted.
          <img src={preview} alt="" className="animate-scale-in size-full object-cover" />
        ) : (
          <>
            <ImageUp
              className="size-5 transition-transform duration-[--duration-base] ease-[--ease-out-soft] group-hover:scale-110"
              aria-hidden="true"
            />
            {placeholder ? (
              <span className="text-sm">{placeholder}</span>
            ) : (
              <span>
                Upload
                <br />
                Image
              </span>
            )}
          </>
        )}
        <input
          id={id}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={pick}
          className="sr-only"
        />
      </label>

      {error ? (
        <p role="alert" className="animate-slide-up text-danger text-xs">
          {error}
        </p>
      ) : null}
    </div>
  )
}
